import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FieldType, FieldOption, FieldOptionFetchResponse } from 'types/FieldTypes';
import { generateFieldComponents } from 'components/forms/GenerateFieldComponents';
import FormFieldContext from 'components/forms/FormFieldContext';
import SecondaryButton from 'components/buttons/SecondaryButton';
import PrimaryButton from 'components/buttons/PrimaryButton';
import { useModal } from 'components/modals/ModalContext';
import { fetchData } from 'services/api/fetchData';
import { saveData } from 'services/api/saveData';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useGlobalContext } from 'GlobalContext';
import { languageOptions, localeOptions } from 'internationalization/i18n';

// The field names that are saved to the division settings
const SETTINGS_FIELD_NAMES = ['default_currency', 'default_language', 'default_locale'];

interface CompanyDetailsEditFormProps {
    viewKey?: string;
    division?: { id: number, is_main: boolean, name?: string };
}

// Modal form to edit the company details, logo and general settings of a division
const CompanyDetailsEditForm: React.FC<CompanyDetailsEditFormProps> = ({ viewKey, division }) => {
    const { t } = useTranslation();
    const { environmentHash, activeFeatures, handleLogout } = useAuthContext();
    const { setFloatingAlert, errorMessages, setErrorMessages } = useGlobalContext();
    const { revealModal, closeModal, modalStack } = useModal();
    const [editing, setEditing] = useState(true);
    const [formData, setFormData] = useState<Record<string, any>>({});
    const [updatedData, setUpdatedData] = useState<Record<string, any>>({});
    const [dropdownData, setDropdownData] = useState<Record<string, FieldOptionFetchResponse>>({});
    const [buttonLoader, setButtonLoader] = useState(false);
    const topModal = modalStack[modalStack.length - 1];

    // Determine whether the modal edits the main division
    const isMain = !division || division.is_main;

    // Function to convert hardcoded options into dropdown options
    function convertOptionsToDropdownOptions(options: Record<string, { label: string }>): FieldOption[] {
        return Object.entries(options).map(([value, { label }]) => ({
            value,
            name: label,
        }))
    };

    // Convert language and locale options into dropdown options
    const languageDropdownOptions: FieldOption[] = convertOptionsToDropdownOptions(languageOptions);
    const localeDropdownOptions: FieldOption[] = convertOptionsToDropdownOptions(localeOptions);

    // Load the company details, settings and dropdown data of the division
    useEffect(() => {
        const loadData = async () => {
            try {
                // Fetch the company details of the division
                const companyDetails = await fetchData({
                    apiUrl: isMain
                        ? `get_main_company_details/${environmentHash}`
                        : `get_division_company_details/${division!.id}`,
                    handleLogout
                });

                // Fetch the environment settings with the division values overlayed
                const settings = await fetchData({
                    apiUrl: `get_environment_settings/${environmentHash}`,
                    params: !isMain ? { division: division!.id } : undefined,
                    handleLogout
                });

                // Fetch the currency options for the currency dropdown
                const currencies = await fetchData({
                    apiUrl: 'get_currency_list',
                    handleLogout
                });
                setDropdownData({ 'currency': currencies });

                // Merge the fetched data into one form data object
                setFormData({
                    ...(division ? { name: division.name } : {}),
                    ...companyDetails,
                    default_currency: settings?.default_currency,
                    default_language: settings?.default_language,
                    default_locale: settings?.default_locale,
                });

                // Reveal the modal now the data is loaded
                revealModal();
            } catch (error) {
                console.error("Error loading company details", error);
                revealModal();
            }
        };
        loadData();
    }, []);

    // Configure the fields of the form
    const fields: FieldType[] = [
        // Show the division name field for divisions other than the main division
        // The company name of a division always follows the division name
        ...(!isMain ? [{
            type: 'text',
            name: 'name',
            label: 'division.general.division_name_label',
            placeholder: 'division.general.division_name_placeholder',
        } as FieldType] : [{
            type: 'text',
            name: 'company_name',
            label: 'company_details.general.company_name_label',
            placeholder: 'company_details.general.company_name_placeholder',
        } as FieldType]),
        {
            name: 'company_logo',
            label: 'company_details.general.company_logo_label',
            type: 'image',
            alignment: 'stacked',
            imageType: 'landscape',
            dropzoneText: 'company_details.general.company_logo_placeholder',
            apiField: {
                fileUpload: 'company_logo_file',
                fileName: 'company_logo_filename',
                imageDeleted: 'company_logo_image_deleted'
            },
            helperText: 'company_details.general.company_logo_helperText',
        },
        {
            type: 'header',
            header: 'company_details.general.address_header'
        },
        {
            type: 'text',
            name: 'street',
            label: 'company_details.general.street_label',
            placeholder: 'company_details.general.street_placeholder',
            width: '70%',
            group: 'field-row',
        },
        {
            type: 'text',
            name: 'house_number',
            label: 'company_details.general.house_number_label',
            placeholder: 'company_details.general.house_number_placeholder',
            width: '30%',
            group: 'field-row',
        },
        {
            type: 'text',
            name: 'postal_code',
            label: 'company_details.general.postal_code_label',
            placeholder: 'company_details.general.postal_code_placeholder',
            width: '40%',
            group: 'field-row-next',
        },
        {
            type: 'text',
            name: 'city',
            label: 'company_details.general.city_label',
            placeholder: 'company_details.general.city_placeholder',
            width: '60%',
            group: 'field-row-next',
        },
        {
            type: 'text',
            name: 'country',
            label: 'company_details.general.country_label',
            placeholder: 'company_details.general.country_placeholder',
        },
        {
            type: 'header',
            header: 'company_details.general.contact_information_header'
        },
        {
            type: 'email',
            name: 'email',
            label: 'company_details.general.email_label',
            placeholder: 'company_details.general.email_placeholder',
        },
        {
            type: 'text',
            name: 'phone_number',
            label: 'company_details.general.phone_number_label',
            placeholder: 'company_details.general.phone_number_placeholder',
        },
        {
            type: 'text',
            name: 'website',
            label: 'company_details.general.website_label',
            placeholder: 'company_details.general.website_placeholder',
        },
        {
            type: 'header',
            header: 'company_details.general.financial_details_header'
        },
        {
            type: 'text',
            name: 'coc_number',
            label: 'company_details.general.kvk_number_label',
            placeholder: 'company_details.general.kvk_number_placeholder',
        },
        {
            type: 'text',
            name: 'vat_number',
            label: 'company_details.general.vat_number_label',
            placeholder: 'company_details.general.vat_number_placeholder',
        },
        {
            type: 'text',
            name: 'iban_number',
            label: 'company_details.general.iban_number_label',
            placeholder: 'company_details.general.iban_number_placeholder',
        },
        {
            type: 'header',
            header: 'company_details.general.settings_header'
        },
        {
            type: 'dropdown',
            name: 'default_currency',
            label: 'company_details.general.default_currency_label',
            disabled_selected: 'forms.general_disabled_selected',
            allowNoneOption: false,
            apiRequest: [{
                endpoint: 'get_currency_list',
                object: 'currency',
                params: {},
                useCache: true
            }],
            backendField: 'key',
            defaultValue: { value: formData?.default_currency || 'EUR' }
        },
        {
            type: 'dropdown',
            name: 'default_language',
            label: 'company_details.general.default_language_label',
            disabled_selected: 'forms.general_disabled_selected',
            allowNoneOption: false,
            hardcodedOptions: languageDropdownOptions as { value: string; name: string }[],
            defaultValue: { value: formData?.default_language }
        },
        {
            type: 'dropdown',
            name: 'default_locale',
            label: 'company_details.general.default_locale_label',
            disabled_selected: 'forms.general_disabled_selected',
            allowNoneOption: false,
            hardcodedOptions: localeDropdownOptions as { value: string; name: string }[],
            defaultValue: { value: formData?.default_locale }
        },
    ];

    // Generate the field components for the form
    const fieldComponents = useMemo(() => {
        if (!viewKey) return;
        return generateFieldComponents(
            viewKey,
            fields,
            dropdownData,
            undefined,
            formData,
            updatedData,
            activeFeatures
        );
    }, [updatedData, formData, dropdownData]);

    // Handle the submit of the updated company details and settings
    const handleSubmit = async () => {
        try {
            // Start the button loader
            setButtonLoader(true);

            // Show error when the company name of the main division is emptied
            if (isMain && 'company_name' in updatedData && !updatedData.company_name) {
                setErrorMessages({ 'company_name': 'validation.general.required' });
                setButtonLoader(false);
                return;
            }

            // Show error when the division name is emptied
            if (!isMain && 'name' in updatedData && !updatedData.name) {
                setErrorMessages({ 'name': 'validation.general.required' });
                setButtonLoader(false);
                return;
            }

            // Split the updated data into company, settings and name values
            const companyData: Record<string, any> = {};
            const settingsData: Record<string, any> = {};
            let divisionName: string | undefined = undefined;
            for (const [key, value] of Object.entries(updatedData)) {
                if (SETTINGS_FIELD_NAMES.includes(key)) {
                    settingsData[key] = value;
                } else if (key === 'name') {
                    divisionName = value;
                } else {
                    companyData[key] = value;
                }
            }

            // Patch the company details of the division
            if (Object.keys(companyData).length > 0) {
                await saveData({
                    apiUrl: isMain
                        ? `patch_main_company_details/${environmentHash}`
                        : `patch_division_company_details/${division!.id}`,
                    method: 'patch',
                    data: companyData
                });
            }

            // Patch the settings of the division
            if (Object.keys(settingsData).length > 0) {
                await saveData({
                    apiUrl: isMain
                        ? `patch_environment_settings/${environmentHash}`
                        : `patch_division_settings/${division!.id}`,
                    method: 'patch',
                    data: settingsData
                });
            }

            // Patch the division name when it is changed
            if (divisionName !== undefined && division) {
                await saveData({
                    apiUrl: `patch_division/${division.id}`,
                    method: 'patch',
                    data: { name: divisionName }
                });
            }

            // Stop the button loader
            setButtonLoader(false);

            // Show success alert
            setFloatingAlert({ 'type': 'success' })

            // Call the on success callback
            if (topModal?.props?.onSuccess) {
                topModal.props.onSuccess();
            };

            // Close the modal
            closeModal()

            // Remove the error messages
            setErrorMessages({});
        } catch (error) {
            console.error("Error on submit", error);
            setButtonLoader(false);
        };
    };

    // Close the modal
    const handleClose = () => {
        setButtonLoader(false);
        setErrorMessages({});
        closeModal();
    }

    return (
        <FormFieldContext.Provider value={{ editing, setEditing, updatedData, setUpdatedData }}>
            {(errorMessages.general) &&
                <div className="alert form-alert alert-danger" role="alert">
                    {t(errorMessages.general, { defaultValue: errorMessages.general })}
                </div>
            }
            <form className='formset add-edit-form'>
                {fieldComponents}
                <div className='buttons-right'>
                    <SecondaryButton
                        onClick={handleClose}
                        label='general.cancel'
                        size="small"
                    />
                    <PrimaryButton
                        type="button"
                        label='general.save'
                        onClick={handleSubmit}
                        size="small"
                        onlyViewRestriction={true}
                        loading={buttonLoader}
                    />
                </div>
            </form>
        </FormFieldContext.Provider>
    )
};

export default CompanyDetailsEditForm;
